import React from 'react';
import { Box, Badge, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { useMutation } from 'react-query';
import toast from 'react-hot-toast';
import axios from 'api/request';
import { endPoints } from 'api/endpoints';
import { getLoggedUserClaims, getLoggedUserFullName } from 'utils';
import EnvironmentDetails from 'pages/provisioning/_components/EnvironmentDetails';
import { format } from 'date-fns';
import { sspSubmitOrder, environmentDetails, toggleSspModels, isMandatoryTagsFullfilled, isEnvDetailsLoading } from 'pages/provisioning/store/event-hub';
// import ProvisioningStyles from 'pages/provisioning/event-hub/style';
import { commonStyles as style } from 'assets/style';
import Btn from 'components/button';

const EnvironmentContainer = () => {
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const [envDetails, setEnvDetails] = useRecoilState(environmentDetails);
    const [, setLoading] = useRecoilState(isEnvDetailsLoading);
    const resetEnvDetails = useResetRecoilState(environmentDetails);
    const [modal, setModal] = useRecoilState(toggleSspModels);
    const [isMandatoryTags, setIsMandatoryTags] = useRecoilState(isMandatoryTagsFullfilled);

    const envDetailsDataApi = useMutation((payload) => axios.post(endPoints.ssp.vm.environmentDetails_v2, payload));
    const tagsApi = useMutation(() => axios.get(endPoints.ssp.vm.mandatoryTags));
    const tagsBasedOnResoureGroupApi = useMutation(() => axios.get(`${endPoints.ssp.vm.resourceGroupTags}${order?.envDetails?.config?.resourceGroup}/tags`));
    const userClaims = getLoggedUserClaims();

    const checkVarConditions = () =>
        order?.appProfile && order?.environment && order?.region && order?.cloudProvider && order?.cloudService && order?.landingZoneDetails;

    React.useEffect(() => {
        if (tagsApi?.data?.data?.values) {
            const tagsValue = tagsApi.data.data.values.map((tag) => {
                if (tag?.name === 'Environment' && (!tag?.editableOnBlank || !tag.editable)) {
                    return { ...tag, defaultValue: order?.environment };
                }
                if (tag?.name === 'ApplicationService' && (!tag.editableOnBlank || !tag.editable)) {
                    return { ...tag, defaultValue: order?.appProfile };
                }
                if (tag?.name === 'HoursOfOperation' && (!tag.editableOnBlank || !tag.editable)) {
                    if (order?.tagsQc?.HoursOfOperation) {
                        return { ...tag, defaultValue: order?.tagsQc?.HoursOfOperation };
                    }
                    return { ...tag, defaultValue: 'Always ON' };
                }
                if (tag?.name === 'Migration') {
                    if (order?.tagsQc?.Migration) {
                        return { ...tag, defaultValue: order?.tagsQc?.Migration };
                    }
                    return tag;
                }
                if (tag?.name === 'FinancialTag' && (!tag.editableOnBlank || !tag.editable)) {
                    return tag;
                }
                if (tag?.name === 'Servicename' && (!tag.editableOnBlank || !tag.editable)) {
                    if (order?.tagsQc?.Servicename) {
                        return { ...tag, defaultValue: order?.tagsQc?.Servicename };
                    }
                    return tag;
                }
                if (tag?.name === 'Requestedby' && (!tag.editableOnBlank || !tag.editable)) {
                    return { ...tag, defaultValue: getLoggedUserFullName() };
                }
                if (tag?.name === 'Provisioneddate' && (!tag.editableOnBlank || !tag.editable)) {
                    return { ...tag, defaultValue: format(new Date(), 'yyyy-MM-dd') };
                }
                if (tag?.name === 'Sector' && (!tag.editableOnBlank || !tag.editable)) {
                    return { ...tag, defaultValue: order?.sector || '-' };
                }
                if (tag?.name === 'Billingentity' && (!tag.editableOnBlank || !tag.editable)) {
                    return { ...tag, defaultValue: order?.billingEntity || '-' };
                }
                if (tag?.name === 'Costcenter' && (!tag.editableOnBlank || !tag.editable)) {
                    return { ...tag, defaultValue: order?.hCode ? order?.hCode : '-' };
                }
                return tag;
            });
            setOrder({ ...order, tags: tagsValue, defaultTags: tagsValue });
        }
        // eslint-disable-next-line
    }, [tagsApi.data?.data, order?.tagsQc]);

    React.useEffect(() => {
        if (tagsBasedOnResoureGroupApi?.isSuccess) tagsApi.mutate();
    }, [tagsBasedOnResoureGroupApi?.isSuccess]);

    React.useEffect(() => {
        setIsMandatoryTags(false);
        if (order?.tags?.length) {
            const tagsMandateCheck = order?.tags.filter((tag) => tag.defaultValue === '' && tag.editableOnBlank)?.length > 0;
            setIsMandatoryTags(tagsMandateCheck);
        }
        // eslint-disable-next-line
    }, [order?.tags]);

    React.useEffect(() => {
        tagsBasedOnResoureGroupApi.reset();
        if (checkVarConditions()) {
            setLoading(true);
            envDetailsDataApi.mutate({
                landingZoneId: order?.landingZoneDetails?.id,
            });
        } else {
            resetEnvDetails();
        }
    }, [order?.landingZoneDetails]);

    React.useEffect(() => {
        if (checkVarConditions() && order?.envDetails?.config?.resourceGroup) {
            tagsBasedOnResoureGroupApi.mutate();
        }
        // eslint-disable-next-line
    }, [order?.appProfile, order?.environment, order?.appTier, order?.region, order?.envDetails?.config?.resourceGroup]);

    React.useEffect(() => {
        if (checkVarConditions() && envDetailsDataApi?.isSuccess) {
            const env = envDetailsDataApi?.data?.data;
            if (!env) {
                toast.error('Environmet details is not available.');
            }
            if (env) {
                const envData = {
                    requestorGpid: userClaims?.accessToken?.claims?.gpid,
                    requestorName: getLoggedUserFullName(),
                    envDetails: {
                        ...env,
                    },
                };
                setOrder({
                    ...order,
                    ...envData,
                });
                setEnvDetails({
                    Subscription: env?.config?.subscription?.name,
                    Resource_Group: env?.config?.resourceGroup,
                    Approval_Manager: env?.applicationManager,
                    Data_Clasification: env?.config?.dataClassification?.displayName,
                    Project: order?.hCodeDetails?.projectName ? order?.hCodeDetails?.projectName : '-',
                    'TLS Version': '1.2',
                    ...(order?.environment === 'Production' &&
                        order?.isGeoReplication === 'yes' && { Geo_Replication_Region: env?.config?.geoRecoveryRegion?.name }),
                    Service_Endpoints: (
                        <TableContainer>
                            <span>
                                <b>Selected Networks</b>
                            </span>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Vnet</TableCell>
                                        <TableCell>Subnet</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {env?.config?.vnetSubnet?.map((z, i) => (
                                        <TableRow key={i}>
                                            <TableCell component="th" scope="row" sx={{ color: '#000000' }}>
                                                {z?.vnet?.name}
                                            </TableCell>
                                            <TableCell sx={{ color: '#000000' }}>{z?.subnet}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ),
                });
            }
            setLoading(false);
        }
    }, [envDetailsDataApi?.isSuccess, order?.isGeoReplication]);

    return (
        <>
            <EnvironmentDetails envs={envDetails} isLoading={envDetailsDataApi?.isLoading}>
                <Box>
                    {order?.region && order?.tags?.length !== 0 && (
                        <>
                            <Badge color="error" variant={isMandatoryTags ? 'dot' : null} sx={style.tagBadge}>
                                <Btn
                                    className="btn-disabled custom-btn"
                                    onClick={() => setModal({ ...modal, isTagOpen: true })}
                                    variant="contained"
                                    disableElevation
                                    sx={style.serviceNewTagBtn}
                                    data-testid="tags-btn"
                                >
                                    Tags*
                                </Btn>
                            </Badge>
                        </>
                    )}
                    <Btn
                        className="btn-disabled custom-btn"
                        disabled={!order?.appProfile}
                        onClick={() =>
                            order?.appProfile
                                ? setModal({
                                      ...modal,
                                      isApplicationOpen: true,
                                  })
                                : false
                        }
                        variant="outlined"
                        disableElevation
                        sx={style.secondarySmallButton}
                        data-testid="app-details"
                    >
                        Application Details
                    </Btn>
                </Box>
            </EnvironmentDetails>
        </>
    );
};

export default EnvironmentContainer;
